<script>
import { inject, reactive, ref, computed } from "vue";
import _head from "lodash/head";
import { useI18n } from "vue-i18n";
import { success } from "@/use/toast/success";
import { error } from "@/use/toast/error";
import { isCurrentDepartment } from "@/use/utilities/isCurrentDepartment";
import { print } from "@/use/repositories/devices/print";
import { signedUrl } from "@/use/repositories/media/signedUrl";
import { index as fetchAllTesWorkQueue } from "@/use/repositories/testWorkQueue/index";
import { getPrescriptionScanName, copyPrescriptionScanName } from '@/utils/prescriptionScanName';
import ShippingCode from "@/components/Consulting/ShippingCode.vue";
import Spinner from "@/components/general/Spinner";
import Empty from "@/components/Modal/Empty";
import VideoPlayer from "@/components/checkout/VideoPlayer";

export default {
  name: "HistoryActions",
  components: { ShippingCode, Empty, VideoPlayer, Spinner },
  props: {
    prescriptionTest: {
      type: Object,
      required: true,
    },
    prescriptionDetails: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { states, devicesType, departments } = inject("constants");
    const i18n = useI18n();
    const dataVideo = reactive({
      url: null,
      type: 'video/mp4',
    });
    const showVideoModal = ref(false);
    const isPrescriptionScanNameCopied = ref(false);
    const isCopying = ref(false);
    const normalizeMedia = (media) => Array.isArray(media) ? _head(media) : media;
    const approvalVideoMedia = computed(() => normalizeMedia(props.prescriptionTest.approval_video_media));
    const readMediaUuid = (media) => {
      const item = normalizeMedia(media);
      return typeof item === 'string' ? item : item?.uuid;
    };
    const readMediaUrl = (media) => {
      const item = normalizeMedia(media);
      return typeof item === 'object' ? item?.temporary_url : null;
    };
    const readMediaType = (media, url = '') => {
      const item = normalizeMedia(media);
      const value = [
        typeof item === 'object' ? item?.mime_type : null,
        typeof item === 'object' ? item?.mime : null,
        typeof item === 'object' ? item?.file_name : null,
        url,
      ].find(Boolean) || '';
      return String(value).toLowerCase().includes('webm') ? 'video/webm' : 'video/mp4';
    };

    const printBarcode = async () => {
      try {
        const response = await fetchAllTesWorkQueue(
          {
            action: "with_base_64_labels",
            ids: [props.prescriptionTest.test_work_queue.id],
          },
          true
        );

        await print({
          device_type_slug: devicesType.ANALOG_LABEL,
          department_slug: departments.LOGISTICS,
          data: _head(response.testWorkQueue).raw_data,
        });

        success(i18n.t("Barcodes print successfully!"));
      } catch (error) {
        console.error("printBarcode error:", error);
      }
    };

    const showVideo = async (media) => {
      try {
        const temporaryUrl = readMediaUrl(media);
        if (temporaryUrl) {
          dataVideo.url = temporaryUrl;
          dataVideo.type = readMediaType(media, temporaryUrl);
          showVideoModal.value = true;
          return;
        }
        const uuid = readMediaUuid(media);
        if (!uuid) return;
        const { url } = await signedUrl(uuid);
        dataVideo.url = url;
        dataVideo.type = readMediaType(media, url);
        showVideoModal.value = true;
      } catch (error) {
        console.error("showVideo error:", error);
      }
    };

    const prescriptionScanName = computed(() => {
      if (!props.prescriptionDetails || Object.keys(props.prescriptionDetails).length === 0 || !props.prescriptionTest?.test_work_queue?.id) {
        return '';
      }
      try {
        return getPrescriptionScanName(props.prescriptionDetails, props.prescriptionTest.test_work_queue.id);
      } catch (err) {
        console.error("prescriptionScanName: Error in getPrescriptionScanName:", err);
        return '';
      }
    });

    const copyPrescriptionScanNameHandler = async () => {
      if (!prescriptionScanName.value) {
        error(i18n.t("No file name to copy"));
        isCopying.value = false;
        return;
      }

      isCopying.value = true;
      try {
        await copyPrescriptionScanName(prescriptionScanName.value, {
          i18n: i18n.t,
          success,
          error,
        });
        setTimeout(() => {
          isCopying.value = false;
          isPrescriptionScanNameCopied.value = true;
        }, 1000);
      } catch (err) {
        console.error("copyPrescriptionScanNameHandler: Copy failed:", err);
        error(i18n.t("Failed to copy file name"));
        isCopying.value = false;
      }
    };

    return {
      states,
      isCurrentDepartment,
      departments,
      showVideoModal,
      dataVideo,
      showVideo,
      approvalVideoMedia,
      printBarcode,
      prescriptionScanName,
      copyPrescriptionScanName: copyPrescriptionScanNameHandler,
      isPrescriptionScanNameCopied,
      isCopying,
    };
  },
};
</script>

<template>
  <div class="d-flex flex-column">
    <div class="d-flex">
      <template v-if="prescriptionTest.shipping_codes.length">
        <ShippingCode
          v-for="(shippingCode, index) in prescriptionTest.shipping_codes"
          :key="index"
          :shipping-code="shippingCode"
          icon-only
        />
      </template>
      <template v-if="prescriptionTest.media?.length">
        <div class="d-flex align-items-center me-2">
          <span
            @click="showVideo(prescriptionTest.media[0])"
            class="material-icons-outlined me-2 md-24">
            play_circle
          </span>
        </div>
      </template>
      <template v-if="isCurrentDepartment(departments.LOGISTICS) && prescriptionTest.is_current_test">
        <div class="d-flex align-items-center me-2">
          <img
            class="me-2"
            src="@/assets/icons/barcode.svg"
            width="25"
            alt="check-circle-fill-green" />
          <small class="text-decoration-underline text-nowrap" @click="printBarcode()">{{ $t('print label') }}</small>
        </div>
      </template>
    </div>
    <div class="d-flex flex-column">
      <template v-if="[states.SHIPPED, states.NEGATIVE_FEEDBACK].includes(prescriptionTest.state_slug)">
        <div class="test-info d-flex flex-wrap mt-1">
          <small class="d-flex align-items-center me-2 text-nowrap">
            <span class="material-icons-outlined me-2">local_shipping</span>
            <span>{{ prescriptionTest.checkout_date }}</span>
          </small>
        </div>
      </template>
      <template v-if="prescriptionTest.delivery_date">
        <small class="d-flex align-items-center me-2 text-nowrap fw-bold">
          <span class="material-icons-outlined me-2"> flag </span>
          {{ prescriptionTest.delivery_date }}
        </small>
      </template>
      <template v-if="prescriptionTest.created_at">
        <small class="d-flex align-items-center me-2 text-nowrap">
          <span class="material-icons-outlined me-2"> qr_code_scanner </span>
          {{ prescriptionTest.created_at ?? "" }}
        </small>
      </template>
      <div class="d-none">{{ prescriptionTest.id }}</div>
      <template v-if="prescriptionTest.is_current_test">
        <div v-if="approvalVideoMedia" class="d-flex align-items-center me-2">
          <span
            @click="showVideo(approvalVideoMedia)"
            class="material-icons-outlined me-2 md-24 cursor-pointer"
            title="Video Approvazione ISO">
            play_circle
          </span>
          <small class="text-decoration-underline text-nowrap dark-violet fw-bold" @click="showVideo(approvalVideoMedia)">
            Video Approvazione ISO
          </small>
        </div>
        <div v-if="!isPrescriptionScanNameCopied && !isCopying" class="d-flex align-items-center me-2">
          <img
            class="me-2 cursor-pointer"
            src="@/assets/icons/copy.svg"
            width="25"
            alt="copy-icon"
            @click="copyPrescriptionScanName" />
          <small class="text-decoration-underline text-nowrap dark-violet fw-bold" @click="copyPrescriptionScanName">{{ $t('Copy file name') }}</small>
        </div>
        <Spinner v-if="isCopying" class="ms-2" />
        <div v-if="isPrescriptionScanNameCopied" class="prescription-scan-footer d-flex justify-content-center mt-2">
          <small class="green fw-bold">{{ prescriptionScanName }}</small>
          <img class="ms-1 cursor-pointer" src="@/assets/icons/copy.svg" width="20" alt="copy" @click="copyPrescriptionScanName" />
        </div>
      </template>
    </div>

    <!-- Video modal -->
    <Teleport to="body">
      <Empty
        v-if="showVideoModal"
        :size="'xl'"
        :modal_id="`${prescriptionTest?.id}-video-modal`"
        @hidden="() => showVideoModal = false" >
        <VideoPlayer
          :options="{
            autoplay: true,
            controls: true,
            fluid: true,
            sources: [{ src: dataVideo.url, type: dataVideo.type }],
          } " />
      </Empty>
    </Teleport>
  </div>
</template>

<style scoped>
.prescription-scan-footer {
  text-align: center;
  overflow: hidden;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.prescription-scan-footer small {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 125px;
  margin: 0 5px 0 0;
  display: inline-block;
}
</style>
